import * as tslib_1 from "tslib";
import { AfterViewInit, ChangeDetectorRef, ElementRef, ErrorHandler, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatTab, MatTabGroup } from '@angular/material';
import { Meta, Title } from '@angular/platform-browser';
import { debounceTime, filter, tap } from 'rxjs/operators';
import * as debounce from 'lodash/debounce';
import { NEIGHBORSTATES } from '../../util/neighbor-states';
import { ResultsMapComponent } from '../results-map/results-map.component';
import { SearchFormType } from '../models/search-form-type.model';
import { SearchQuery } from '../models/search-query.model';
import { State } from '../../shared/state-view/state.model';
import { QueryValidationReason } from '../models/query-validation-result.model';
import { FilterKeys } from '../filters/filters';
import { SearchFormComponent } from '../search-form/search-form.component';
import { AirportService, MAX_BBOX_AREA } from '../../airport/airport.service';
import { GoogleService } from '../../google/google.service';
import { AirportResultsListingComponent } from '../airport-results-listing/airport-results-listing.component';
import { ActivityResultsListingComponent } from '../activity-results-listing/activity-results-listing.component';
import { EventResultsListingComponent } from '../event-results-listing/event-results-listing.component';
import { environment } from '../../../environments/environment';
import { BusinessFuel, FuelType } from '../../business/models/business-fuel.model';
import { GlobalSettingsService } from '../../shared/services/global-settings.service';
import { AviationEventType } from '../../shared/models/aviation-event.model';
import { AviationEventsService } from '../../shared/services/aviation-events.service';
import { AviationEventFiltersService } from '../../shared/services/aviation-event-filters.service';
import * as moment from 'moment-timezone';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
import { AuthService } from '../../auth/auth/auth.service';
/**
 * Period of time to wait (in milliseconds) before issuing a new bounding box search after panning or zooming
 * the map.
 */
var SEARCH_DEBOUNCE_TIME = 500;
var SearchPageComponent = /** @class */ (function () {
    function SearchPageComponent(_route, _router, _meta, _title, _airportService, _google, _cdref, _errorHandler, _globalSettingsService, _aviationEventsService, _aviationEventFiltersService, _snackBar, _analyticsService, _auth) {
        this._route = _route;
        this._router = _router;
        this._meta = _meta;
        this._title = _title;
        this._airportService = _airportService;
        this._google = _google;
        this._cdref = _cdref;
        this._errorHandler = _errorHandler;
        this._globalSettingsService = _globalSettingsService;
        this._aviationEventsService = _aviationEventsService;
        this._aviationEventFiltersService = _aviationEventFiltersService;
        this._snackBar = _snackBar;
        this._analyticsService = _analyticsService;
        this._auth = _auth;
        this.query = new SearchQuery();
        this.sidebarOpened = true;
        this.airports = [];
        this.activities = [];
        this.aviationEvents = [];
        this.airportState = State.Empty;
        this.activityState = State.Empty;
        this.aviationEventsState = State.Empty;
        this.pageIndex = 0;
        this.pageSize = 10;
        this.filteredAirports = [];
        this.filteredAviationEvents = [];
        this.declutteredAirports = [];
        this.locationState = State.Empty;
        this.pauseBounds = false;
        this.mapMessage = null;
        this._selectedAirportId = null;
        this.$sidebar = null;
        this.backToSearchVisible = false;
        this._handler = null;
        this.fuelType = FuelType._100LL;
        this.NEIGHBORSTATES = NEIGHBORSTATES;
        this.exceedsAirportBounds = false;
        this.eventPageIndex = 0;
        this.eventPageSize = 25;
        this._title.setTitle("Airport Search - " + environment.appName);
        this._meta.removeTag('name="description"');
    }
    Object.defineProperty(SearchPageComponent.prototype, "searchType", {
        get: function () {
            var tab = this._getSelectedTab();
            if (tab === this.activitiesTab) {
                return SearchFormType.Activities;
            }
            else if (tab === this.aviationEventsTab) {
                return SearchFormType.AviationEvents;
            }
            else {
                if (this.query && this.query.type && this.query.type === SearchFormType.FuelPrice) {
                    return SearchFormType.FuelPrice;
                }
                else {
                    return SearchFormType.Airports;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SearchPageComponent.prototype, "toggleTooltip", {
        get: function () {
            return this.sidebarOpened ? 'Collapse sidebar' : 'Expand sidebar';
        },
        enumerable: true,
        configurable: true
    });
    SearchPageComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._initBoundsListener();
        this.fuelTypeSubscription = this._globalSettingsService.fuelType.subscribe(function (fuel) {
            _this.fuelType = BusinessFuel.parseFuelType(fuel);
        });
        this.aviationEventFilterSubscription = this._aviationEventFiltersService.aviationEventFilter.subscribe(function (ae) {
            _this.aviationEventFilter = ae;
        });
        this._analyticsService.pageView('airport_search', 'Airport/Event Search');
        if (this._auth.authenticated && this._auth.endUser) {
            this._analyticsService.trackEvent('airport_search_loggedin', this._auth.recid, this._auth.recid);
        }
        else {
            this._analyticsService.trackEvent('airport_search_loggedout', '', '');
        }
    };
    SearchPageComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        // Set the initial tab based on search type
        var query = SearchQuery.decode(this._route.snapshot.queryParamMap);
        if (query.type === SearchFormType.Activities) {
            this.tabGroup.selectedIndex = 1;
        }
        if (query.type === SearchFormType.AviationEvents) {
            this.tabGroup.selectedIndex = 2;
            this._aviationEventFiltersService.updateAviationEventFilters(query.aviationEventsFilter);
        }
        // Wrapped in setTimeout to avoid triggering change detection error
        // ("Expression has changed after it was checked").
        setTimeout(function () {
            // Store the initially selected tab.
            _this.selectedTab = _this._getSelectedTab();
            // Deserialize the search query from the URL
            _this.deserializeQuery();
            // Perform the initial search, but only if the selected tab is the default one. Otherwise,
            // rely on the onSelectTab event handler to perform the initial search.
            _this.searchAirports(false);
            if (_this.tabGroup.selectedIndex === 1) {
                _this.searchActivities();
            }
            if (_this.tabGroup.selectedIndex === 2) {
                _this.searchEvents(false);
            }
        }, 0);
        // Initialize page resize/scroll listener, which updates the visibility of a "Back to Search" button that
        // allows getting back to the search form. This is primarily useful on mobile, where a user might zoom in
        // and scroll the page into the map - at which point, scrolling back is not possible since swiping results
        // in panning the map instead of scrolling.
        // @ts-ignore
        this.$sidebar = $('.search-page__sidebar');
        this._handler = debounce(this.updateBackToSearch.bind(this), 300);
        setTimeout(function () { return _this.updateBackToSearch(); }, 300);
        // @ts-ignore
        $(window).on('resize scroll', this._handler);
    };
    SearchPageComponent.prototype.ngOnDestroy = function () {
        if (this._handler) {
            // @ts-ignore
            $(window).off('resize scroll', this._handler);
        }
        this.aviationEventFilterSubscription.unsubscribe();
    };
    SearchPageComponent.prototype.onSelectTab = function (event) {
        this.mapMessage = null;
        this.selectedTab = event.tab;
        this.serializeQuery();
        if (this.selectedTab === this.airportsTab) {
            this.activities = [];
            this.aviationEvents = [];
            this.filteredAviationEvents = [];
            this.searchAirports();
            this.onFiltersChanged();
        }
        // Reload airports (if not already loaded), regardless of which tab is open.
        if (this.airportState === State.Empty || this.airportState === State.Error) {
            this.searchAirports();
            this.onFiltersChanged();
        }
        // Reload activities (if not already loaded), but only if the activities tab is open.
        if (this.selectedTab === this.activitiesTab) {
            this.aviationEvents = [];
            this.filteredAviationEvents = [];
            this.searchActivities();
            this.onFiltersChanged();
        }
        // Reload aviationEvents (if not already loaded), but only if the events tab is open.
        if (this.selectedTab === this.aviationEventsTab) {
            this.activities = [];
            this.searchEvents();
            this.onFiltersChanged();
        }
    };
    SearchPageComponent.prototype._getSelectedTab = function () {
        var _this = this;
        if (!this.tabGroup._tabs) {
            return null;
        }
        return this.tabGroup._tabs
            .toArray()
            .find(function (tab, i) { return i === _this.tabGroup.selectedIndex; });
    };
    /**
     * Returns the minimum difference in the latitude or longitude of either corner of the viewport before considering the
     * new viewport to be different from the previous and issuing a new search. This is based on the previous viewport,
     * and is dependent on the scale (a difference of 0.1 degrees may be negligible when zoomed out, but significant
     * when zoomed in, so we cannot use a constant value).
     * @param bounds {BoundingBox} Viewport bounding box to use as the basis for calculating the epsilon value
     * @returns {number} Epsilon value
     */
    SearchPageComponent.prototype.getBoundingBoxEpsilon = function (bounds) {
        return 0.05 * Math.min(Math.abs(bounds.ne.lat - bounds.sw.lat), Math.abs(bounds.ne.lon - bounds.sw.lon));
    };
    Object.defineProperty(SearchPageComponent.prototype, "pagedAirports", {
        get: function () {
            if (!this.filteredAirports) {
                return [];
            }
            var start = this.pageIndex * this.pageSize;
            return this.filteredAirports.slice(start, start + this.pageSize);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SearchPageComponent.prototype, "pagedAviationEvents", {
        get: function () {
            if (!this.filteredAviationEvents) {
                return [];
            }
            var start = this.eventPageIndex * this.eventPageSize;
            return this.filteredAviationEvents.slice(start, start + this.eventPageSize);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SearchPageComponent.prototype, "includePrivate", {
        get: function () {
            if (!this.query) {
                return false;
            }
            var f = this.query.getFilter(FilterKeys.PublicUse);
            return !f.enabled;
        },
        enumerable: true,
        configurable: true
    });
    SearchPageComponent.prototype.search = function (keepPriceSliderState) {
        /** This forces all state queries to search by airport and switches back to the airport tab */
        if (this.query.state && (this._getSelectedTab() === this.activitiesTab || this._getSelectedTab() === this.aviationEventsTab)) {
            this.tabGroup.selectedIndex = 0;
        }
        if (!this.exceedsAirportBounds && (this.selectedTab !== this.aviationEventsTab || this.eventsResultsListing.showAirports)) {
            this.searchAirports(keepPriceSliderState);
        }
        if (this.query.type === SearchFormType.Activities) {
            this.searchActivities();
        }
        if (this.query.type === SearchFormType.AviationEvents) {
            this.searchEvents(keepPriceSliderState);
        }
    };
    SearchPageComponent.prototype.searchAirports = function (keepPriceSliderState) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var validationResult, _a, _b, _c, e_1, _d, e_2;
            return tslib_1.__generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        _e.trys.push([0, 15, , 16]);
                        validationResult = this.query.validate();
                        if (!validationResult.valid) return [3 /*break*/, 13];
                        this.airportState = State.Loading;
                        this.form.disableForm();
                        this.serializeQuery();
                        if (!this.query.term) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this._airportService.getAirportsBySearchTerm(this.query.term, this.includePrivate)];
                    case 1:
                        _a.airports = _e.sent();
                        return [3 /*break*/, 12];
                    case 2:
                        if (!(this.query.location && this.query.radius)) return [3 /*break*/, 4];
                        _b = this;
                        return [4 /*yield*/, this._airportService.getAirportsByLatLon(this.query.location, this.query.radius, this.includePrivate)];
                    case 3:
                        _b.airports = _e.sent();
                        return [3 /*break*/, 12];
                    case 4:
                        if (!this.query.bounds) return [3 /*break*/, 9];
                        _e.label = 5;
                    case 5:
                        _e.trys.push([5, 7, , 8]);
                        _c = this;
                        return [4 /*yield*/, this._airportService.getAirportsByBoundingBox(this.query.bounds, this.includePrivate)];
                    case 6:
                        _c.airports = _e.sent();
                        return [3 /*break*/, 8];
                    case 7:
                        e_1 = _e.sent();
                        if (e_1.message.includes('Bounding box area')) {
                            this.mapMessage = 'Zoom in to update results';
                        }
                        else {
                            throw e_1;
                        }
                        return [3 /*break*/, 8];
                    case 8: return [3 /*break*/, 12];
                    case 9:
                        if (!this.query.state) return [3 /*break*/, 11];
                        _d = this;
                        return [4 /*yield*/, this._airportService.getAirportsByState(this.query.state, this.includePrivate)];
                    case 10:
                        _d.airports = _e.sent();
                        this.activities = [];
                        this.query.state = null;
                        return [3 /*break*/, 12];
                    case 11:
                        if (this.query.type === SearchFormType.AviationEvents) {
                            this.airports = [];
                        }
                        else {
                            throw new Error('Either a search term, a location and radius, or a bounding box is required to search airports.');
                        }
                        _e.label = 12;
                    case 12:
                        this.filteredAirports = this.query.execute(this.airports);
                        this._restoreSelectedAirport();
                        this.airportState = State.Content;
                        this.form.enableForm();
                        if (!this.query.bounds) {
                            this._updateBounds();
                        }
                        this.onFiltersChanged(keepPriceSliderState);
                        this.errorMessage = null;
                        return [3 /*break*/, 14];
                    case 13:
                        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
                            this.airportState = State.Empty;
                            return [2 /*return*/];
                        }
                        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
                        this.errorMessage = validationResult.message;
                        this.airportState = State.Error;
                        _e.label = 14;
                    case 14: return [3 /*break*/, 16];
                    case 15:
                        e_2 = _e.sent();
                        this._errorHandler.handleError(e_2);
                        this.airports = [];
                        this.filteredAirports = [];
                        this.form.enableForm();
                        this.errorMessage = null;
                        this.airportState = State.Error;
                        return [3 /*break*/, 16];
                    case 16: return [2 /*return*/];
                }
            });
        });
    };
    SearchPageComponent.prototype.searchActivities = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var validationResult, result, e_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 8, , 9]);
                        validationResult = this.query.validate();
                        if (!validationResult.valid) return [3 /*break*/, 6];
                        if (!this.query.activity) {
                            this.activityState = State.Empty;
                            return [2 /*return*/];
                        }
                        this.activityState = State.Loading;
                        this.form.disableForm();
                        this.serializeQuery();
                        result = void 0;
                        if (!(this.query.location && this.query.radius)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this._google.searchActivitiesByLatLon(this.query.activity, this.query.location, this.query.radius)];
                    case 1:
                        result = _a.sent();
                        return [3 /*break*/, 5];
                    case 2:
                        if (!this.query.bounds) return [3 /*break*/, 4];
                        return [4 /*yield*/, this._google.searchActivitiesByBoundingBox(this.query.activity, this.query.bounds)];
                    case 3:
                        result = _a.sent();
                        return [3 /*break*/, 5];
                    case 4: throw new Error('Either a location and radius or a bounding box is required to search activities');
                    case 5:
                        this.activities = result.places;
                        this.activityState = State.Content;
                        this.form.enableForm();
                        if (!this.query.bounds) {
                            this._updateBounds();
                        }
                        this.errorMessage = null;
                        return [3 /*break*/, 7];
                    case 6:
                        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
                            this.activityState = State.Empty;
                            return [2 /*return*/];
                        }
                        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
                        this.errorMessage = validationResult.message;
                        this.activityState = State.Error;
                        _a.label = 7;
                    case 7: return [3 /*break*/, 9];
                    case 8:
                        e_3 = _a.sent();
                        this._errorHandler.handleError(e_3);
                        this.activities = [];
                        this.form.enableForm();
                        this.errorMessage = null;
                        this.activityState = State.Error;
                        return [3 /*break*/, 9];
                    case 9: return [2 /*return*/];
                }
            });
        });
    };
    SearchPageComponent.prototype.onAviationEventsFiltersChanged = function () {
        this.serializeQuery();
        this.filteredAviationEvents = this.filterAviationEvents(this.aviationEvents);
    };
    SearchPageComponent.prototype.onAviationEventsFiltersDateChanged = function () {
        this.serializeQuery();
        this.searchEvents();
        // this.filteredAviationEvents = this.filterAviationEvents(this.aviationEvents);
    };
    SearchPageComponent.prototype.filterAviationEvents = function (aviationEvents) {
        var _this = this;
        var filteredEvents = [];
        aviationEvents.forEach(function (aviationEvent) {
            if (aviationEvent.type === AviationEventType.Airshow && _this.query.aviationEventsFilter.airshow === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.AirshowwithMilitaryDemonstrationTeam &&
                _this.query.aviationEventsFilter.airshowwithmilitarydemonstrationteam === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.FlyIn && _this.query.aviationEventsFilter.flyin === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.FlyOut && _this.query.aviationEventsFilter.flyout === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.PancakeBreakfast && _this.query.aviationEventsFilter.pancakeBreakfast === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.SocialGathering && _this.query.aviationEventsFilter.socialGathering === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.Openhouse && _this.query.aviationEventsFilter.openhouse === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.PokerRun && _this.query.aviationEventsFilter.pokerRun === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.AviationCommunityDay && _this.query.aviationEventsFilter.aviationCommunityDay === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.WingsAndWheelsEvent && _this.query.aviationEventsFilter.wingsAndWheels === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.AircraftDisplayRides && _this.query.aviationEventsFilter.aircraftDisplayRides === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.RustyPilotsSeminar && _this.query.aviationEventsFilter.aopaRustyPilots === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.FlyingClubSeminar && _this.query.aviationEventsFilter.aopaFlyingClub === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.SafetySeminar && _this.query.aviationEventsFilter.safetySeminar === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.Conference && _this.query.aviationEventsFilter.conference === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.Workshop && _this.query.aviationEventsFilter.workshop === true) {
                filteredEvents.push(aviationEvent);
            }
            if (aviationEvent.type === AviationEventType.BackToYourRoots && _this.query.aviationEventsFilter.aopaBacktoYourRoots === true) {
                filteredEvents.push(aviationEvent);
            }
        });
        var queryStart = moment(new Date(this.query.aviationEventsFilter.startDate).setHours(0, 0, 0, 0));
        var queryEnd = moment(new Date(this.query.aviationEventsFilter.endDate).setHours(23, 59, 59, 999));
        filteredEvents = filteredEvents.filter(function (ae) {
            var eventStart = moment(new Date(ae.startDateTime), 'UTC').tz(ae.timezone);
            var eventEnd = moment(new Date(ae.endDateTime), 'UTC').tz(ae.timezone);
            if ((eventStart >= queryStart && eventStart <= queryEnd) || (eventEnd >= queryStart && eventEnd <= queryEnd)) {
                return true;
            }
            else {
                return false;
            }
        });
        filteredEvents = filteredEvents.filter(function (ae) {
            return ae.title.toLowerCase().includes(_this.query.aviationEventsFilter.name.toLowerCase());
        });
        filteredEvents = filteredEvents.filter(function (ae) {
            if (_this.query.aviationEventsFilter.formatOnline && (ae.format === 'Online' || ae.format === 'Both')) {
                return true;
            }
            else if (_this.query.aviationEventsFilter.formatInPerson && (ae.format === 'In-Person' || ae.format === 'Both') &&
                (ae.location && ae.location.lat && ae.location.lon)) {
                return true;
            }
            else {
                return false;
            }
        });
        // check to see if there are any visible in-person events
        var visibleInPersonEvents = filteredEvents.some(function (ae) {
            if ((ae.format === 'Both' || ae.format === 'In-Person') && ae.location && ae.location.lat && ae.location.lon) {
                return true;
            }
            return false;
        });
        if (visibleInPersonEvents) {
            if (this.mapMessage && this.mapMessage.startsWith('Zoom in to update ')) {
            }
            else {
                this.mapMessage = null;
            }
        }
        else {
            if (this.query.aviationEventsFilter.formatOnline && !this.query.aviationEventsFilter.formatInPerson) {
                this.mapMessage = 'You have filtered out In-Person events';
            }
            else if (this.query.aviationEventsFilter.formatInPerson) {
                this.mapMessage = 'No In-Person events to show in this area';
            }
        }
        return filteredEvents;
    };
    SearchPageComponent.prototype.searchEvents = function (keepPriceSliderState) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var validationResult, queryStartDate, queryStartDateUTC, queryEndDate, queryEndDateUTC, _a, _b, _c, _d, e_4;
            return tslib_1.__generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        _e.trys.push([0, 11, , 12]);
                        validationResult = this.query.validate();
                        if (!validationResult.valid) return [3 /*break*/, 9];
                        this.aviationEventsState = State.Loading;
                        this.form.disableForm();
                        this.serializeQuery();
                        queryStartDate = moment(new Date(this.query.aviationEventsFilter.startDate).setHours(0, 0, 0, 0));
                        queryStartDateUTC = queryStartDate.clone().tz('UTC').format();
                        queryEndDate = moment(new Date(this.query.aviationEventsFilter.endDate).setHours(23, 59, 59, 999));
                        queryEndDateUTC = queryEndDate.clone().tz('UTC').format();
                        if (!(this.query.location && this.query.radius)) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this._aviationEventsService.getAviationEventsByLatLon(this.query.location, this.query.radius, queryStartDateUTC, queryEndDateUTC)];
                    case 1:
                        _a.aviationEvents = _e.sent();
                        if (!this.query.bounds) {
                            this._updateBounds();
                        }
                        return [3 /*break*/, 8];
                    case 2:
                        if (!this.query.bounds) return [3 /*break*/, 4];
                        _b = this;
                        return [4 /*yield*/, this._aviationEventsService.getAviationEventsByBoundingBox(this.query.bounds, queryStartDateUTC, queryEndDateUTC)];
                    case 3:
                        _b.aviationEvents = _e.sent();
                        return [3 /*break*/, 8];
                    case 4:
                        if (!this.query.state) return [3 /*break*/, 6];
                        _c = this;
                        return [4 /*yield*/, this._aviationEventsService.getAviationEventsByState(this.query.state, queryStartDateUTC, queryEndDateUTC)];
                    case 5:
                        _c.aviationEvents = _e.sent();
                        this.activities = [];
                        this.query.state = null;
                        return [3 /*break*/, 8];
                    case 6:
                        _d = this;
                        return [4 /*yield*/, this._aviationEventsService.getAviationEvents(queryStartDateUTC, queryEndDateUTC)];
                    case 7:
                        _d.aviationEvents = _e.sent();
                        _e.label = 8;
                    case 8:
                        this.filteredAviationEvents = this.filterAviationEvents(this.aviationEvents);
                        this.aviationEventsState = State.Content;
                        this.form.enableForm();
                        /* if (!this.query.bounds) {
                          this._updateBounds();
                        } */
                        this.onFiltersChanged(keepPriceSliderState);
                        this.errorMessage = null;
                        return [3 /*break*/, 10];
                    case 9:
                        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
                            this.aviationEventsState = State.Empty;
                            return [2 /*return*/];
                        }
                        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
                        this.errorMessage = validationResult.message;
                        this.aviationEventsState = State.Error;
                        _e.label = 10;
                    case 10: return [3 /*break*/, 12];
                    case 11:
                        e_4 = _e.sent();
                        this._errorHandler.handleError(e_4);
                        this.aviationEvents = [];
                        this.filteredAviationEvents = [];
                        this.form.enableForm();
                        this.errorMessage = null;
                        this.aviationEventsState = State.Error;
                        return [3 /*break*/, 12];
                    case 12: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Restores the selected airport after performing a search, causing the card to reopen. If the user clicks on an
     * airport, we center the map on the airport and open the card. However, re-centering the map triggers a new search,
     * which clears the markers and causes the card to close. We want to restore the card after the search completes.
     * @private
     */
    SearchPageComponent.prototype._restoreSelectedAirport = function () {
        var _this = this;
        if (!this._selectedAirportId) {
            return;
        }
        setTimeout(function () {
            _this.selectedAirport = _this.filteredAirports.find(function (a) { return a.airportId === _this._selectedAirportId; }) || null;
            // Clear the airport ID. This is mainly to prevent the card from reopening in case it was closed manually. We can't
            // differentiate between the card closing because the user deliberately closed it, vs it getting closed because a
            // new search was triggered after centering the map on the clicked airport.
            _this._selectedAirportId = null;
        }, 300);
    };
    SearchPageComponent.prototype._updateBounds = function () {
        var _this = this;
        this.pauseBounds = true;
        this.map.updateBounds();
        setTimeout(function () {
            _this.pauseBounds = false;
        }, 1000);
    };
    SearchPageComponent.prototype.onAirportCardHover = function (airport, hover) {
        this.activeAirport = airport;
        this.map.toggleAirportInfoTooltip(airport, hover);
    };
    SearchPageComponent.prototype.onActivityCardHover = function (activity, hover) {
        if (hover) {
            this.selectedActivity = activity;
        }
    };
    SearchPageComponent.prototype.onEventCardHover = function (aviationEvent, hover) {
        this.activeAviationEvent = aviationEvent;
        this.map.toggleAviationEventInfoTooltip(aviationEvent, hover);
    };
    SearchPageComponent.prototype.onAirportMarkerHover = function (airport, hover) {
        this.activeAirport = hover ? airport : null;
        if (hover && this.selectedTab === this.airportsTab) {
            this.airportResultsListing.scrollToAirport(airport);
        }
    };
    SearchPageComponent.prototype.onEventMarkerHover = function (aviationEvent, hover) {
        this.activeAviationEvent = hover ? aviationEvent : null;
        if (hover && this.selectedTab === this.aviationEventsTab) {
            this.eventsResultsListing.scrollToEvent(aviationEvent);
        }
    };
    SearchPageComponent.prototype.onAirportClick = function (airport) {
        this.selectedAirport = airport;
        this._selectedAirportId = airport.airportId;
        this.map.toggleAirportInfoTooltip(airport, false); // Close tooltip window
        this.map.setCenter(airport.location);
    };
    SearchPageComponent.prototype.onCloseAirportCardWindow = function () {
        var _this = this;
        setTimeout(function () { return _this.selectedAirport = null; }, 0);
    };
    SearchPageComponent.prototype.onCloseActivityWindow = function (activity) {
        var _this = this;
        setTimeout(function () {
            if (_this.selectedActivity === activity) {
                _this.selectedActivity = null;
            }
        }, 0);
    };
    SearchPageComponent.prototype.onActivityMarkerHover = function (activity, hover) {
        if (hover) {
            this.selectedActivity = activity;
            if (this.selectedTab === this.activitiesTab) {
                this.activityResultsListing.scrollToActivity(activity);
            }
        }
    };
    SearchPageComponent.prototype.onActivityClick = function (activity) {
        // do nothing
    };
    SearchPageComponent.prototype.toggleSidebar = function () {
        this.sidebarOpened = !this.sidebarOpened;
        // Initially hide the Back to Search button so it doesn't overlay the sidebar if it is being opened.
        // It may end up being shown again after a delay in onSidebarOpenedChange.
        this.backToSearchVisible = false;
    };
    SearchPageComponent.prototype.onSidebarOpenedChange = function () {
        var _this = this;
        setTimeout(function () {
            _this.map.updateSize();
            _this.updateBackToSearch();
        }, 300);
    };
    SearchPageComponent.prototype.serializeQuery = function () {
        this.query.type = this.searchType;
        this.query.aviationEventsFilter = this.aviationEventFilter;
        var params = this.query.encode();
        this._router.navigate([], { queryParams: params, replaceUrl: true });
    };
    SearchPageComponent.prototype.deserializeQuery = function () {
        this.query = SearchQuery.decode(this._route.snapshot.queryParamMap);
        if ((this.query.location || this.query.state) && this.query.locationDescription) {
            this.form.setQueryFieldValue(this.query.locationDescription);
        }
    };
    SearchPageComponent.prototype.onFiltersChanged = function (keepPriceSliderState) {
        var _this = this;
        this.serializeQuery();
        this.filteredAirports = this.query.execute(this.airports);
        if (this.query.bounds) {
            var declutterDistance = this.calculateDeclutterDistance(this.query.bounds.getAreaDegrees());
            if (declutterDistance > 0) {
                this.declutteredAirports = this.declutterMapMarkers(this.filteredAirports, declutterDistance);
            }
            else {
                this.declutteredAirports = this.filteredAirports;
            }
        }
        else {
            this.declutteredAirports = this.filteredAirports;
        }
        var anyFuelPriceFilterApplied = this.airportResultsListing.airportFilters.fuel100LLFilter.enabled
            || this.airportResultsListing.airportFilters.fuelJetAFilter.enabled
            || this.airportResultsListing.airportFilters.fuelMOGASFilter.enabled
            || this.airportResultsListing.airportFilters.fuelUL94Filter.enabled;
        // toggle on and off automatically when clicking fuel price filter
        if (keepPriceSliderState != null) {
            if (!keepPriceSliderState && anyFuelPriceFilterApplied) {
                this.airportResultsListing.showPrices = true;
            }
            else if (!keepPriceSliderState && !anyFuelPriceFilterApplied) {
                this.airportResultsListing.showPrices = false;
            }
        }
        if (anyFuelPriceFilterApplied && this.airportResultsListing.showPrices) {
            if (!this.declutteredAirports || this.declutteredAirports.length < 1) {
                return;
            }
            // divide min fuel prices for each airport into high, med, and low
            this.declutteredAirports.forEach(function (a) { return a.minPrice = _this.getMinFuel(a); });
            var lowMedIndexThreshold_1 = Math.ceil(this.declutteredAirports.length / 3);
            var medHighIndexThreshold_1 = this.declutteredAirports.length === 2 ? lowMedIndexThreshold_1 : lowMedIndexThreshold_1 * 2;
            this.declutteredAirports.sort(function (a, b) { return a.minPrice - b.minPrice; });
            this.declutteredAirports.forEach(function (a, i) { return a.minPriceType = _this.getMinPriceType(i, lowMedIndexThreshold_1, medHighIndexThreshold_1); });
        }
        else {
            // reset minPrice and minPriceType to null
            this.declutteredAirports.forEach(function (a) {
                a.minPrice = null;
                a.minPriceType = null;
            });
        }
    };
    SearchPageComponent.prototype.getMinFuel = function (airport) {
        var _this = this;
        if ((airport.fuel || []).length < 1) {
            return null;
        }
        var minFuel = airport.fuel
            .filter(function (f) {
            if (_this.fuelType === FuelType.JetA && (f.fuelType === FuelType.JetA || f.fuelType === FuelType.JetAPremix)) {
                return true;
            }
            return f.fuelType === _this.fuelType;
        })
            .sort(function (f1, f2) { return f1.price - f2.price; })[0];
        return minFuel ? minFuel.price : null;
    };
    SearchPageComponent.prototype.getMinPriceType = function (index, lowMediumThreshold, mediumHighTreshold) {
        if (index < 0 || lowMediumThreshold == null || mediumHighTreshold == null) {
            return null;
        }
        if (index >= mediumHighTreshold) {
            return 'High';
        }
        else if (index < mediumHighTreshold && index >= lowMediumThreshold) {
            return 'Medium';
        }
        else {
            return 'Low';
        }
    };
    SearchPageComponent.prototype.calculateDeclutterDistance = function (areaDegrees) {
        if (areaDegrees < 15) {
            return 0;
        }
        else if (areaDegrees >= 15 && areaDegrees < 50) {
            return 17;
        }
        else if (areaDegrees >= 50 && areaDegrees < 130) {
            return 22;
        }
        else {
            return 27;
        }
    };
    SearchPageComponent.prototype.declutterMapMarkers = function (airportMarkers, declutterDistance) {
        if (airportMarkers.length < 1) {
            return airportMarkers;
        }
        var toReturn = [];
        var markersTooClose = [];
        var distance = 0;
        for (var i = 0; i < airportMarkers.length; i++) {
            var _loop_1 = function (j) {
                // if marker is already too close to another marker, skip it
                var marker = markersTooClose.find(function (a) { return a.airportId === airportMarkers[j].airportId; });
                if (marker != null) {
                    return "continue";
                }
                // for performance, assume if an airport is not in a neighboring state, it is far enough and we don't have to calc the distance
                if (!this_1.areStatesSameOrNeighbors(airportMarkers[i].stateProvince, airportMarkers[j].stateProvince)) {
                    return "continue";
                }
                distance = airportMarkers[i].location.distanceTo(airportMarkers[j].location) * 0.000621371; // convert meters to miles
                if (distance < declutterDistance) {
                    // if marker is too close add it to the list of too close markers
                    markersTooClose.push(airportMarkers[j]);
                }
            };
            var this_1 = this;
            for (var j = i + 1; j < airportMarkers.length; j++) {
                _loop_1(j);
            }
        }
        // return the original array minus any markers that are too close
        toReturn = airportMarkers.filter(function (a) { return !markersTooClose.map(function (b) { return b.airportId; }).includes(a.airportId); });
        return toReturn;
    };
    SearchPageComponent.prototype.areStatesSameOrNeighbors = function (state1, state2) {
        var toReturn = false;
        if (state1 == null || state2 == null) {
            return toReturn;
        }
        if (state1 === state2) {
            toReturn = true;
            return toReturn;
        }
        var state = this.NEIGHBORSTATES.find(function (s) { return s.code === state1; });
        if (state != null) {
            var neighborStates = state.neighborCodes;
            toReturn = neighborStates.indexOf(state2) !== -1;
            return toReturn;
        }
    };
    SearchPageComponent.prototype.onPublicUseChanged = function () {
        this.searchAirports();
    };
    SearchPageComponent.prototype.onFuelPriceToggleChanged = function () {
        var anyFuelPriceFilterApplied = this.airportResultsListing.airportFilters.fuel100LLFilter.enabled
            || this.airportResultsListing.airportFilters.fuelJetAFilter.enabled
            || this.airportResultsListing.airportFilters.fuelMOGASFilter.enabled
            || this.airportResultsListing.airportFilters.fuelUL94Filter.enabled;
        if (!anyFuelPriceFilterApplied && this.airportResultsListing.showPrices) {
            this._snackBar.open('Please select a fuel type in the Airport Amenities list to display fuel prices', '', {
                duration: 6000
            });
        }
        this.onFiltersChanged();
    };
    SearchPageComponent.prototype.onSelectedActivityChange = function () {
        this.serializeQuery();
        this.searchActivities();
    };
    SearchPageComponent.prototype.onLocationStateChanged = function (state) {
        this.locationState = state;
        if (this.query.type !== SearchFormType.Activities) {
            this.activityState = State.Empty;
        }
        if (state !== State.Content) {
            this.activities = [];
        }
    };
    SearchPageComponent.prototype.onShowAirportsToggleChanged = function () {
        // show or remove pins from display as appropriate
        if (this.eventsResultsListing.showAirports) {
            if (this.exceedsAirportBounds) {
                this.mapMessage = 'Zoom in to update airports displayed';
            }
            this.search(true);
        }
        else {
            // showAirports is false, so call the filter to apply it
            if (this.mapMessage === 'Zoom in to update airports displayed') {
                this.mapMessage = null;
            }
            this.onFiltersChanged();
        }
    };
    /**
     * Initializes the observable that determines when to issue a new search after the map bounds are updated.
     */
    SearchPageComponent.prototype._initBoundsListener = function () {
        var _this = this;
        // Set up the boundsChange$ observable
        this.boundsChange$ = this.map.boundsChange.pipe(
        // Wait until bounds have stabilized before issuing a new search
        debounceTime(SEARCH_DEBOUNCE_TIME), 
        // Avoid issuing a new search if we programmatically updated the map bounds
        filter(function () { return !_this.pauseBounds; }), 
        // Avoid issuing a new search if this is the first update we received for the map bounds
        filter(function (b) {
            // Record whether we have a previous set of bounds
            var haveBounds = !!_this.prevBounds;
            // If we don't have an initial set of bounds, store the current bounds so we have something to compare to
            // next time around.
            if (!haveBounds) {
                _this.prevBounds = b;
            }
            // Return whether we had prevBounds
            return haveBounds;
        }), 
        // Avoid issuing a search if the viewport bounding box area exceeds MAX_BBOX_AREA
        filter(function (b) {
            var area = b.getAreaDegrees();
            var exceedsBounds = area > MAX_BBOX_AREA;
            _this.exceedsAirportBounds = exceedsBounds;
            if (_this.query.type === SearchFormType.AviationEvents) {
                if (_this.eventsResultsListing.showAirports) {
                    _this.mapMessage = exceedsBounds ? 'Zoom in to update airports displayed' : null;
                    return true;
                }
                else {
                    return true;
                }
            }
            _this.mapMessage = exceedsBounds ? 'Zoom in to update results' : null;
            return !exceedsBounds;
        }), 
        // Ensure the new bounding box is sufficiently different from the previous before issuing a search
        filter(function (b) { return !_this.prevBounds || !b.equalTo(_this.prevBounds, _this.getBoundingBoxEpsilon(_this.prevBounds)); }), 
        // Store the new bounding box in prevBounds so we can compare it later
        tap(function (b) { return _this.prevBounds = b; }));
        // Subscribe to the boundsChange$ observable and issue a new search every time it emits an event.
        this.boundsChange$.subscribe(function (b) { return _this.onBoundsChanged(b); });
    };
    SearchPageComponent.prototype.onBoundsChanged = function (bounds) {
        // If we are doing a term search, then ignore the event.
        if (this.query.term && this.query.type !== SearchFormType.AviationEvents) {
            return;
        }
        // Update the search query parameters with the new bounding box.
        this.query.location = null;
        this.query.radius = null;
        this.query.bounds = bounds;
        this.serializeQuery();
        // If we're only searching airports, clear out any old activity results.
        if (this.query.type !== SearchFormType.Activities) {
            this.activities = [];
            this.activityState = State.Empty;
        }
        /* if (this.query.type !== SearchFormType.AviationEvents) {
          this.aviationEvents = [];
          this.aviationEventsState = State.Empty;
        } */
        // Perform a new search
        this.search(true);
    };
    SearchPageComponent.prototype.onPageChanged = function (e) {
        this.pageIndex = e.pageIndex;
    };
    SearchPageComponent.prototype.onEventPageChanged = function (e) {
        this.eventPageIndex = e.pageIndex;
    };
    SearchPageComponent.prototype.updateBackToSearch = function () {
        this.backToSearchVisible = !this.$sidebar['visible'](true);
        this._cdref.detectChanges();
        if (this.backToSearchVisible) {
            this.backToSearchEl.nativeElement.style.top = (window.pageYOffset + 4) + 'px';
            this.backToSearchEl.nativeElement.style.left = (window.pageXOffset + 24) + 'px';
        }
    };
    SearchPageComponent.prototype.backToSearch = function () {
        this.backToSearchVisible = false;
        window.scroll(0, 0);
        this.sidebarOpened = true;
    };
    SearchPageComponent.prototype.onClickNewSearch = function (event) {
        event.preventDefault();
        if (this.selectedTab === this.aviationEventsTab) {
            this._router.navigate(['/'], { queryParams: { 't': 'events' } });
        }
        else if (this.selectedTab === this.activitiesTab) {
            this._router.navigate(['/'], { queryParams: { 't': 'activities' } });
        }
        else {
            if (this.query && this.query.type && this.query.type === SearchFormType.FuelPrice) {
                this._router.navigate(['/'], { queryParams: { 't': 'fuel' } });
            }
            else {
                this._router.navigate(['/'], { queryParams: { 't': 'airports' } });
            }
        }
    };
    SearchPageComponent.prototype.onError = function (message) {
        this.errorMessage = message;
        if (this.selectedTab === this.airportsTab) {
            this.airportState = State.Error;
        }
        else {
            this.activityState = State.Error;
        }
    };
    return SearchPageComponent;
}());
export { SearchPageComponent };
